import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Select,
  Spacer,
  Tooltip,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import type { ReactElement } from 'react'
import { useState, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type { RemoveBgErrorResponse } from '../../../interfaces'
import type {
  ExtensionStatus,
  FileDetailsResponse,
} from '../../../interfaces/FileDetailsResponse'
import { FeatureNameMapping, FeaturesEnum } from '../../../../utils/features'
import { UserContext } from '../../../providers'

interface BG_REMOVE {
  name: 'remove-bg'
  options?: {
    add_shadow?: boolean
    semitransparency?: boolean
    bg_color?: string
    bg_image_url?: string
  }
}

interface FormProps {
  background: string
  shadowCheck: boolean
  semiTransparentCheck: boolean
  bgColor: string
  bgImageURL: string
}

const RemoveBgModal = ({
  isOpen,
  fileDetails,
  fileId,
  onClose,
  setRemoveBgData,
  removeBgData,
  multipleFile
}: {
  isOpen: boolean
  fileDetails?: FileDetailsResponse
  fileId?: string
  onClose(): void
  setRemoveBgData?: (removeBgData: FormProps) => void
  removeBgData?: FormProps
  multipleFile?: boolean
}): ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [demoImageURL, updateDemoImageURL] = useState(
    'https://ik.imagekit.io/ikmedia/tr:w-410/shadow_false_semitrans_false.png',
  )

  const getModifiedDemoImageURL = (
    isShadowChecked,
    isSemiTransparentChecked,
  ): string => {
    if (isShadowChecked && isSemiTransparentChecked) {
      return 'https://ik.imagekit.io/ikmedia/tr:w-410/shadow_true_semitrans_true.png'
    } else if (isShadowChecked && !isSemiTransparentChecked) {
      return 'https://ik.imagekit.io/ikmedia/tr:w-410/shadow_true_semitrans_false.png'
    } else if (!isShadowChecked && isSemiTransparentChecked) {
      return 'https://ik.imagekit.io/ikmedia/tr:w-410/shadow_false_semitrans_true.png'
    }
    return 'https://ik.imagekit.io/ikmedia/tr:w-410/shadow_false_semitrans_false.png' // !isShadowChecked && !isSemiTransparentChecked
  }

  const showAppropriateToast = ({
    extensionStatus,
  }: {
    extensionStatus: ExtensionStatus | undefined
  }): void => {
    if (extensionStatus && extensionStatus['remove-bg'] === 'failed') {
      toast({
        description: t(
          'bg_remove_error',
          'Something went wrong! Background removal failed.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (extensionStatus && extensionStatus['remove-bg'] === 'pending') {
      toast({
        description: t(
          'bg_remove_job_submitted',
          'Your request has been successfully submitted, a new image in you media libraray will be uploaded in some time if there is no error.',
        ),
        status: 'info',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      onClose()
    }
  }

  useEffect(() => {
    if (removeBgData?.shadowCheck || removeBgData?.semiTransparentCheck)
      updateDemoImageURL(
        getModifiedDemoImageURL(
          removeBgData?.shadowCheck,
          removeBgData?.semiTransparentCheck,
        ),
      )
  }, [removeBgData, isOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay zIndex={2151} />
      <ModalContent
        // z-index 2151 to render above uppy modal
        containerProps={{ zIndex: 2151 }}
      >
        <Formik<FormProps>
          initialValues={
            removeBgData
              ? removeBgData
              : {
                  background: 'no_background',
                  shadowCheck: false,
                  semiTransparentCheck: false,
                  bgColor: colors.appBlue,
                  bgImageURL: '',
                }
          }
          enableReinitialize={true}
          onSubmit={async values => {
            if (setRemoveBgData) {
              setRemoveBgData(values)
              onClose()
              return
            }
            if (!fileDetails && !fileId) {
              return
            }

            let bgRemoveObj: BG_REMOVE = {
              name: 'remove-bg',
            }

            if (values.shadowCheck) {
              bgRemoveObj = { ...bgRemoveObj, options: { add_shadow: true } }
            }
            if (values.semiTransparentCheck) {
              bgRemoveObj = {
                ...bgRemoveObj,
                options: { semitransparency: true },
              }
            }
            if (values.background === 'background_color') {
              bgRemoveObj = {
                ...bgRemoveObj,
                options: { bg_color: values.bgColor },
              }
            }
            if (values.background === 'background_image') {
              bgRemoveObj = {
                ...bgRemoveObj,
                options: { bg_image_url: values.bgImageURL },
              }
            }

            const updatedFileDetails = (await request.patch(
              `/api/v2/files/${fileDetails?.fileId ?? fileId}/details`,
              {
                extensions: [bgRemoveObj],
              },
            )) as FileDetailsResponse | RemoveBgErrorResponse

            if (request.error) {
              toast({
                description:
                  (updatedFileDetails as RemoveBgErrorResponse).message ??
                  t('something_went_wrong', 'Something went wrong!'),
                status: 'error',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
            } else {
              showAppropriateToast({
                extensionStatus: (updatedFileDetails as FileDetailsResponse)
                  .extensionStatus,
              })
            }
          }}
          validationSchema={Yup.object().shape({
            bgColor: Yup.string().when('background', {
              is: backgroundVal => backgroundVal === 'background_color',
              then: Yup.string().required('Field is required'),
              otherwise: Yup.string(),
            }),
            bgImageURL: Yup.string().when('background', {
              is: backgroundVal => backgroundVal === 'background_image',
              then: Yup.string().required('Field is required'),
              otherwise: Yup.string(),
            }),
          })}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <ModalHeader>
                {t('remove_background', 'Remove background')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Field name="background">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginBottom="30px"
                      isInvalid={Boolean(
                        form.errors.background && form.touched.background,
                      )}
                    >
                      <FormLabel htmlFor="background">
                        {t('background', 'Background')}
                      </FormLabel>
                      <Select
                        value={form.values.background}
                        onChange={e => {
                          form.setFieldValue(field.name, e.target.value)
                        }}
                      >
                        <option value="no_background">No background</option>
                        <option value="background_color">
                          Background Color
                        </option>
                        <option value="background_image">
                          Background Image
                        </option>
                      </Select>
                      <FormErrorMessage marginBottom="-2.0408em">
                        {form.errors.background}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                {values.background === 'background_color' && (
                  <Field name="bgColor">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        marginBottom="30px"
                        isInvalid={Boolean(
                          form.errors.bgColor && form.touched.bgColor,
                        )}
                      >
                        <FormLabel>
                          {t(
                            'bg_color_description',
                            'Enter a hex color code (e.g. #81d4fa) or a color name (e.g. green) to add a solid color background.',
                          )}
                        </FormLabel>
                        <InputGroup>
                          <InputLeftElement
                            children={
                              <Box
                                width="80%"
                                height="80%"
                                borderRadius="4px"
                                backgroundColor={field.value}
                              />
                            }
                          />
                          <Input
                            paddingLeft="40px"
                            type="text"
                            value={field.value}
                            onChange={e => {
                              form.setFieldValue(field.name, e.target.value)
                            }}
                          />
                        </InputGroup>
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.bgColor}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                )}
                {values.background === 'background_image' && (
                  <Field name="bgImageURL">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        marginBottom="30px"
                        isInvalid={Boolean(
                          form.errors.bgImageURL && form.touched.bgImageURL,
                        )}
                      >
                        <FormLabel>
                          {t(
                            'bg_image_description',
                            'Enter an image URL to add as background.',
                          )}
                        </FormLabel>
                        <Input
                          type="text"
                          value={field.value}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value)
                          }}
                        />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.bgImageURL}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                )}

                <Flex marginBottom="30px" alignItems="center">
                  <Field name="shadowCheck">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.shadowCheck && form.touched.shadowCheck,
                        )}
                      >
                        <Checkbox
                          isChecked={form.values.shadowCheck}
                          onChange={e => {
                            updateDemoImageURL(
                              getModifiedDemoImageURL(
                                e.target.checked,
                                values.semiTransparentCheck,
                              ),
                            )
                            form.setFieldValue(field.name, e.target.checked)
                          }}
                        >
                          Add shadows
                        </Checkbox>
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.shadowCheck}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="semiTransparentCheck">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.semiTransparentCheck &&
                            form.touched.semiTransparentCheck,
                        )}
                      >
                        <Checkbox
                          isChecked={form.values.semiTransparentCheck}
                          onChange={e => {
                            updateDemoImageURL(
                              getModifiedDemoImageURL(
                                values.shadowCheck,
                                e.target.checked,
                              ),
                            )
                            form.setFieldValue(field.name, e.target.checked)
                          }}
                        >
                          <Tooltip label="Semitransparency is currently only supported for car windows. Other objects are returned without semitransparency, even if you check this.">
                            Add Semi-transparent
                          </Tooltip>
                        </Checkbox>
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.semiTransparentCheck}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Flex>

                <Flex
                  marginBottom="30px"
                  padding="10px"
                  borderRadius="6px"
                  border={`1px solid ${colors.appClearBlue}`}
                  backgroundColor={colors.appPaleGreyThree}
                >
                  <Box
                    width="205px"
                    objectFit="cover"
                    bg="white"
                    borderRadius="6px"
                  >
                    <Box textAlign="center" padding="10px 0px" width="100%">
                      {t('input_image', 'Input Image')}
                    </Box>
                    <Image
                      borderBottomLeftRadius="6px"
                      borderBottomRightRadius="6px"
                      src="https://ik.imagekit.io/ikmedia/tr:w-410/original.jpeg"
                    />
                  </Box>
                  <Spacer />
                  <Box
                    width="205px"
                    objectFit="cover"
                    bg="white"
                    borderRadius="6px"
                  >
                    <Box textAlign="center" padding="10px 0px" width="100%">
                      {t('output_image', 'Output Image')}
                    </Box>
                    <Image
                      borderBottomLeftRadius="6px"
                      borderBottomRightRadius="6px"
                      src={demoImageURL}
                    />
                  </Box>
                </Flex>
                {user?.pricingPlan.featuresList.includes(
                  FeaturesEnum.EXTENSIONS,
                ) && (
                  <Box>
                    <Alert status="info" minWidth={0}>
                      <AlertIcon />
                      {user?.extensionRateCard["remove-bg"]}{' '}
                      {!multipleFile
                        ? t('credits_will_be_used', 'credits will be used')
                        : t(
                            'credits_will_be_used_per_image',
                            'credits will be used per image',
                          )}
                    </Alert>
                  </Box>
                )}
                {!user?.pricingPlan.featuresList.includes(
                  FeaturesEnum.EXTENSIONS,
                ) && (
                  <Box>
                    <Alert status="info" minWidth={0}>
                      <AlertIcon />
                      <AlertDescription>
                        {FeatureNameMapping[FeaturesEnum.EXTENSIONS]} feature is
                        not available in your current plan.{' '}
                        <Link isExternal href="https://imagekit.io/contact-us/">
                          Contact sales
                        </Link>{' '}
                        to enable this feature.
                      </AlertDescription>
                    </Alert>
                  </Box>
                )}
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button onClick={onClose} variant="outline">
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={
                    isSubmitting ||
                    !user?.pricingPlan.featuresList.includes(
                      FeaturesEnum.EXTENSIONS,
                    )
                  }
                >
                  {removeBgData
                    ? t('save')
                    : t('remove_background', 'Remove Background')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default RemoveBgModal
